import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../services/api";
import { qlAlert, qlConfirm } from "../../utils/dialogs";
import DocumentViewer from "../DocumentViewer";
import { Chargement, Chip, Entete, Fenetre, Ligne, Message } from "../compta/ComptaEspace";
import {
  CANAUX, FORMATS, MULTIPART, STATUTS_CONTENU, TON_CONTENU,
  ajouterJours, chargerContenus, dateHeure, erreurApi, formulaire, heureCameroun, jourCameroun,
  libelleSemaine, lundiDe, nomJour, numeroJour, versChampDateHeure,
} from "./cmOutils";
import "./CommunityEspace.css";

const VIDE = {
  titre: "", campagne_id: "", canal: "facebook", format: "visuel", texte: "", texte_alternatif: "",
  lien_cible: "", parametres_campagne: "", date_souhaitee: "",
};

export function useCampagnes() {
  const [campagnes, setCampagnes] = useState([]);
  const charger = useCallback(() => {
    api.get("cm/campagnes/").then((r) => setCampagnes(r.data.campagnes || [])).catch(() => setCampagnes([]));
  }, []);
  useEffect(charger, [charger]);
  return [campagnes, charger];
}

export function FormContenu({ contenu, onClose, onSaved }) {
  const [campagnes] = useCampagnes();
  const [valeurs, setValeurs] = useState(() => contenu ? {
    ...VIDE, ...Object.fromEntries(Object.keys(VIDE).map((k) => [k, contenu[k] ?? ""])),
    campagne_id: contenu.campagne?.id || "", date_souhaitee: versChampDateHeure(contenu.date_souhaitee),
  } : VIDE);
  const [media, setMedia] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [message, setMessage] = useState("");
  const maj = (cle) => (e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }));

  const enregistrer = async (soumettre) => {
    if (!valeurs.titre.trim()) { setMessage("Le titre est obligatoire."); return; }
    setEnvoi(true); setMessage("");
    try {
      const donnees = formulaire({ ...valeurs, ...(media ? { media } : {}) });
      const r = contenu
        ? await api.put(`cm/contenus/${contenu.id}/`, donnees, MULTIPART)
        : await api.post("cm/contenus/", donnees, MULTIPART);
      let resultat = r.data;
      if (soumettre) resultat = (await api.post(`cm/contenus/${resultat.id}/action/`, { action: "soumettre" })).data;
      if (resultat.validation_annulee) qlAlert("Modification importante : la validation est annulée. Soumettez de nouveau le contenu.", "warning");
      else qlAlert(soumettre ? (resultat.statut === "valide" ? "Contenu validé." : "Contenu envoyé aux super admins pour validation.") : "Brouillon enregistré.", "success");
      onSaved(resultat);
    } catch (err) {
      setMessage(erreurApi(err, "Enregistrement impossible."));
    }
    setEnvoi(false);
  };

  return (
    <Fenetre titre={contenu ? "Modifier le contenu" : "Préparer un contenu"} onClose={onClose}>
      {contenu?.statut === "valide" && (
        <div className="cm-alerte">Ce contenu est validé : modifier le texte, le média ou le lien annulera la validation.</div>
      )}
      <div className="qc-form">
        <label className="qc-full">Titre<input value={valeurs.titre} onChange={maj("titre")} maxLength={200} required /></label>
        <label>Canal<select value={valeurs.canal} onChange={maj("canal")}>{CANAUX.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label>Format<select value={valeurs.format} onChange={maj("format")}>{FORMATS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
        <label>Campagne
          <select value={valeurs.campagne_id} onChange={maj("campagne_id")}>
            <option value="">Aucune</option>
            {campagnes.map((c) => <option key={c.id} value={c.id}>{c.titre}</option>)}
          </select>
        </label>
        <label>Date souhaitée (heure du Cameroun)<input type="datetime-local" value={valeurs.date_souhaitee} onChange={maj("date_souhaitee")} /></label>
        <label className="qc-full">Texte proposé<textarea rows={5} value={valeurs.texte} onChange={maj("texte")} maxLength={5000} /></label>
        <label>Visuel, vidéo ou PDF (25 Mo max.)
          <input type="file" accept="image/*,video/mp4,video/quicktime,video/webm,application/pdf" onChange={(e) => setMedia(e.target.files?.[0] || null)} />
          {contenu?.media_nom && !media && <small>Actuel : {contenu.media_nom}</small>}
        </label>
        <label>Texte alternatif du visuel<input value={valeurs.texte_alternatif} onChange={maj("texte_alternatif")} maxLength={300} /></label>
        <label>Lien cible<input type="url" placeholder="https://www.quicklearning-cmr.com/…" value={valeurs.lien_cible} onChange={maj("lien_cible")} /></label>
        <label>Paramètres de campagne<input placeholder="utm_source=facebook&utm_campaign=…" value={valeurs.parametres_campagne} onChange={maj("parametres_campagne")} maxLength={300} /></label>
      </div>
      <Message texte={message} />
      <div className="qc-actions">
        <button type="button" className="qc-btn" disabled={envoi} onClick={() => enregistrer(false)}>Enregistrer le brouillon</button>
        {(!contenu || contenu.statut === "brouillon" || contenu.statut === "valide") && (
          <button type="button" className="qc-btn qc-btn--primary" disabled={envoi} onClick={() => enregistrer(true)}>
            <i className="bi bi-send"></i>{contenu?.statut === "valide" ? "Enregistrer" : "Enregistrer et faire valider"}
          </button>
        )}
      </div>
    </Fenetre>
  );
}

function Historique({ id, rafraichir }) {
  const [lignes, setLignes] = useState(null);
  useEffect(() => {
    api.get(`cm/historique/contenu/${id}/`).then((r) => setLignes(r.data.historique || [])).catch(() => setLignes([]));
  }, [id, rafraichir]);
  if (lignes === null) return <Chargement />;
  return (
    <ul className="qc-histo">
      {lignes.map((l, i) => (
        <li key={i}><strong>{l.action}</strong> <span>— {l.auteur}, {dateHeure(l.date)}</span>{l.details && <div><span>{l.details}</span></div>}</li>
      ))}
      {!lignes.length && <li><span>Aucune action enregistrée.</span></li>}
    </ul>
  );
}

export function Media({ contenu }) {
  const [document, setDocument] = useState(false);
  if (!contenu.media_url) return null;
  if (contenu.media_type === "image") return <img className="cm-media" src={contenu.media_url} alt={contenu.texte_alternatif || contenu.titre} />;
  if (contenu.media_type === "video") return <video className="cm-media" src={contenu.media_url} controls />;
  return (
    <>
      <button type="button" className="qc-btn qc-btn--sm mt-2" onClick={() => setDocument(true)}><i className="bi bi-file-earmark"></i>Ouvrir le document</button>
      {document && <DocumentViewer titre={contenu.titre} url={contenu.media_url} onClose={() => setDocument(false)} />}
    </>
  );
}

export function FicheContenu({ contenu: initial, onClose, onChange }) {
  const [contenu, setContenu] = useState(initial);
  const [edition, setEdition] = useState(false);
  const [publication, setPublication] = useState({ url_publication: "", date_publication: "" });
  const [correction, setCorrection] = useState("");
  const [message, setMessage] = useState("");
  const [tour, setTour] = useState(0);
  const appliquer = (c) => { setContenu(c); onChange(c); setTour((t) => t + 1); };

  const action = async (corps, ok) => {
    setMessage("");
    try {
      appliquer((await api.post(`cm/contenus/${contenu.id}/action/`, corps)).data);
      qlAlert(ok, "success");
    } catch (err) { setMessage(erreurApi(err)); }
  };
  const decider = async (decision) => {
    try {
      appliquer((await api.post(`cm/contenus/${contenu.id}/decision/`, { decision, commentaire: correction })).data);
    } catch (err) { setMessage(erreurApi(err)); }
  };
  const supprimer = () => qlConfirm("Supprimer ce brouillon ?", async () => {
    try { await api.delete(`cm/contenus/${contenu.id}/`); onChange(null, contenu.id); onClose(); } catch (err) { setMessage(erreurApi(err)); }
  }, { danger: true, okLabel: "Supprimer" });

  if (edition) return <FormContenu contenu={contenu} onClose={() => setEdition(false)} onSaved={(c) => { setEdition(false); appliquer(c); }} />;
  return (
    <Fenetre titre={contenu.titre} onClose={onClose}>
      <div className="qc-detailgrid">
        <div>
          <Ligne label="Canal" valeur={contenu.canal_label} />
          <Ligne label="Format" valeur={contenu.format_label} />
          <Ligne label="Campagne" valeur={contenu.campagne?.titre || "—"} />
          <Ligne label="Date souhaitée" valeur={dateHeure(contenu.date_souhaitee)} />
        </div>
        <div>
          <Ligne label="État" valeur={<Chip ton={TON_CONTENU[contenu.statut]}>{contenu.statut_label}</Chip>} />
          <Ligne label="Préparé par" valeur={contenu.auteur || "—"} />
          <Ligne label="Validé par" valeur={contenu.valide_par || "—"} />
          <Ligne label="Publié le" valeur={contenu.date_publication ? dateHeure(contenu.date_publication) : "—"} />
        </div>
      </div>
      {contenu.commentaire_correction && contenu.statut === "brouillon" && (
        <div className="cm-alerte"><strong>Correction demandée :</strong> {contenu.commentaire_correction}</div>
      )}
      <div className="qc-section-title">Texte proposé</div>
      {contenu.texte ? <div className="cm-texte">{contenu.texte}</div> : <div className="qc-foot">Aucun texte.</div>}
      <Media contenu={contenu} />
      {contenu.lien_cible && <div className="qc-foot">Lien : {contenu.lien_cible}{contenu.parametres_campagne ? ` · ${contenu.parametres_campagne}` : ""}</div>}
      {contenu.url_publication && (
        <div className="qc-foot">Publication : <a href={contenu.url_publication} target="_blank" rel="noopener noreferrer">{contenu.url_publication}</a></div>
      )}

      {contenu.statut === "valide" && (
        <>
          <div className="qc-section-title">Enregistrer la publication réelle</div>
          <div className="qc-form">
            <label>Adresse de la publication<input type="url" placeholder="https://facebook.com/…" value={publication.url_publication}
              onChange={(e) => setPublication((p) => ({ ...p, url_publication: e.target.value }))} /></label>
            <label>Date réelle (vide = maintenant)<input type="datetime-local" value={publication.date_publication}
              onChange={(e) => setPublication((p) => ({ ...p, date_publication: e.target.value }))} /></label>
          </div>
          <div className="qc-foot">La publication sur le réseau se fait à la main ; Quick Learning n'envoie rien aux réseaux sociaux.</div>
        </>
      )}
      {contenu.statut === "a_valider" && !contenu.peut_decider && (
        <div className="cm-alerte">Envoyé dans la messagerie des super admins : en attente de leur décision.</div>
      )}
      {contenu.peut_decider && (
        <textarea className="form-control mt-2" rows={2} placeholder="Correction attendue (pour « Demander une correction »)"
          value={correction} onChange={(e) => setCorrection(e.target.value)} />
      )}
      <Message texte={message} />
      <div className="qc-actions">
        {contenu.statut === "brouillon" && <>
          <button className="qc-btn" onClick={() => setEdition(true)}><i className="bi bi-pencil"></i>Modifier</button>
          <button className="qc-btn qc-btn--primary" onClick={() => action({ action: "soumettre" }, "Contenu envoyé pour validation.")}><i className="bi bi-send"></i>Faire valider</button>
          <button className="qc-btn qc-btn--danger" onClick={supprimer}><i className="bi bi-trash"></i>Supprimer</button>
        </>}
        {contenu.statut === "valide" && <>
          <button className="qc-btn" onClick={() => setEdition(true)}><i className="bi bi-pencil"></i>Modifier</button>
          <button className="qc-btn qc-btn--success" onClick={() => action({ action: "publier", ...publication }, "Publication enregistrée.")}>
            <i className="bi bi-check2-circle"></i>Marquer comme publié</button>
        </>}
        {contenu.peut_decider && <>
          <button className="qc-btn qc-btn--success" onClick={() => decider("valider")}><i className="bi bi-check2-circle"></i>Valider</button>
          <button className="qc-btn" onClick={() => decider("corriger")}><i className="bi bi-pencil"></i>Demander une correction</button>
        </>}
      </div>
      <div className="qc-section-title">Historique</div>
      <Historique id={contenu.id} rafraichir={tour} />
    </Fenetre>
  );
}

// Liens de suivi d'une campagne : un par réseau, à mettre dans les publications.
function LiensSuivi({ campagne, onChange }) {
  const [canal, setCanal] = useState("facebook");
  const [qr, setQr] = useState(null);
  const copier = (url) => {
    (navigator.clipboard?.writeText(url) || Promise.reject()).then(() => qlAlert("Lien copié.", "success")).catch(() => qlAlert(url, "info"));
  };
  const ajouter = async () => {
    try { await api.post(`cm/campagnes/${campagne.id}/liens/`, { canal }); onChange(); } catch (err) { qlAlert(erreurApi(err), "error"); }
  };
  const canaux = [["facebook", "Facebook"], ["instagram", "Instagram"], ["youtube", "YouTube"], ["tiktok", "TikTok"], ["whatsapp", "WhatsApp"], ["site", "Site Quick Learning"], ["autre", "Autre (affiche, SMS…)"]];
  const existants = new Set((campagne.liens || []).map((l) => l.canal));
  return (
    <div className="cm-liens">
      {(campagne.liens || []).map((l) => (
        <div key={l.id} className="cm-lien">
          <span className="cm-lien-canal">{l.canal === "autre" ? "Lien général" : l.canal_label}</span>
          <code title={l.url}>{l.url}</code>
          <span className="cm-lien-chiffres">{l.visites} visite{l.visites > 1 ? "s" : ""} · {l.inscriptions} inscr.</span>
          <button className="qc-btn qc-btn--sm" onClick={() => copier(l.url)}><i className="bi bi-clipboard"></i>Copier</button>
          {l.qr && <button className="qc-btn qc-btn--sm" onClick={() => setQr(qr === l.id ? null : l.id)}><i className="bi bi-qr-code"></i>QR</button>}
          {qr === l.id && l.qr && (
            <div className="cm-lien-qr">
              <img src={l.qr} alt={`QR code du lien ${l.url}`} />
              <a className="qc-btn qc-btn--sm" href={l.qr} download={`qr-${l.code}.png`}><i className="bi bi-download"></i>Télécharger</a>
            </div>
          )}
        </div>
      ))}
      <div className="cm-lien-ajout">
        <select value={canal} onChange={(e) => setCanal(e.target.value)}>
          {canaux.filter(([v]) => !existants.has(v)).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
        </select>
        <button className="qc-btn qc-btn--sm" onClick={ajouter} disabled={existants.size >= canaux.length}>+ Lien pour ce réseau</button>
      </div>
    </div>
  );
}

function Campagnes({ onClose }) {
  const [campagnes, recharger] = useCampagnes();
  const [valeurs, setValeurs] = useState({ titre: "", objectif: "", public: "", message_cle: "", date_debut: "", date_fin: "" });
  const [message, setMessage] = useState("");
  const maj = (cle) => (e) => setValeurs((v) => ({ ...v, [cle]: e.target.value }));
  const creer = async () => {
    try {
      await api.post("cm/campagnes/", valeurs);
      setValeurs({ titre: "", objectif: "", public: "", message_cle: "", date_debut: "", date_fin: "" });
      setMessage("✓ Campagne créée.");
      recharger();
    } catch (err) { setMessage(erreurApi(err)); }
  };
  const supprimer = (c) => qlConfirm(`Supprimer la campagne « ${c.titre} » ?`, async () => {
    try { await api.delete(`cm/campagnes/${c.id}/`); recharger(); } catch (err) { qlAlert(erreurApi(err), "error"); }
  }, { danger: true, okLabel: "Supprimer" });
  return (
    <Fenetre titre="Campagnes" onClose={onClose}>
      {campagnes.map((c) => (
        <div key={c.id} className="cm-campagne-suivi">
          <div className="qc-row">
            <span><strong style={{ color: "var(--qc-ink)" }}>{c.titre}</strong><br />
              {c.date_debut} → {c.date_fin} · {c.nb_publies}/{c.nb_contenus} publiés{c.objectif ? ` · ${c.objectif}` : ""}
              {c.liens?.length > 0 && ` · ${c.visites} visite${c.visites > 1 ? "s" : ""}, ${c.inscriptions} inscription${c.inscriptions > 1 ? "s" : ""}`}</span>
            {!c.nb_contenus && !c.visites && <button className="qc-btn qc-btn--sm qc-btn--danger" onClick={() => supprimer(c)}><i className="bi bi-trash"></i></button>}
          </div>
          <LiensSuivi campagne={c} onChange={recharger} />
        </div>
      ))}
      {!campagnes.length && <div className="qc-empty">Aucune campagne.</div>}
      <div className="qc-section-title">Nouvelle campagne</div>
      <div className="qc-form">
        <label className="qc-full">Titre<input value={valeurs.titre} onChange={maj("titre")} /></label>
        <label>Objectif<input value={valeurs.objectif} onChange={maj("objectif")} placeholder="Ex : visites du site" /></label>
        <label>Public visé<input value={valeurs.public} onChange={maj("public")} placeholder="Ex : élèves et parents" /></label>
        <label>Début<input type="date" value={valeurs.date_debut} onChange={maj("date_debut")} /></label>
        <label>Fin<input type="date" value={valeurs.date_fin} onChange={maj("date_fin")} /></label>
        <label className="qc-full">Message clé<textarea rows={2} value={valeurs.message_cle} onChange={maj("message_cle")} /></label>
      </div>
      <Message texte={message} />
      <div className="qc-actions"><button className="qc-btn qc-btn--primary" onClick={creer}>Créer la campagne</button></div>
    </Fenetre>
  );
}

// Gestion partagée de la fiche ouverte / du formulaire, pour toutes les pages.
export function useFiches(onMaj) {
  const [fiche, setFiche] = useState(null);
  const [nouveau, setNouveau] = useState(false);
  const elements = (
    <>
      {fiche && <FicheContenu contenu={fiche} onClose={() => setFiche(null)} onChange={() => onMaj()} />}
      {nouveau && <FormContenu onClose={() => setNouveau(false)} onSaved={(c) => { setNouveau(false); onMaj(); setFiche(c); }} />}
    </>
  );
  return { ouvrir: setFiche, preparer: () => setNouveau(true), elements };
}

// ── Atelier des contenus ────────────────────────────────────────────────────

export function CMContenusPage() {
  const [contenus, setContenus] = useState(null);
  const [recherche, setRecherche] = useState("");
  const [statut, setStatut] = useState("");
  const [campagnes, setCampagnes] = useState(false);
  const charger = useCallback(() => {
    const params = new URLSearchParams();
    if (statut) params.set("statut", statut);
    if (recherche.trim()) params.set("q", recherche.trim());
    chargerContenus(params.toString() ? `?${params}` : "").then(setContenus).catch(() => setContenus([]));
  }, [statut, recherche]);
  useEffect(() => { const t = setTimeout(charger, 250); return () => clearTimeout(t); }, [charger]);
  const { ouvrir, preparer, elements } = useFiches(charger);

  return (
    <div className="qc">
      <Entete titre="Atelier des contenus" sousTitre="Préparer les textes et visuels, puis les faire valider." />
      <div className="qc-toolbar">
        <input className="qc-search" placeholder="Rechercher un contenu, un thème…" value={recherche} onChange={(e) => setRecherche(e.target.value)} aria-label="Rechercher un contenu" />
        <select value={statut} onChange={(e) => setStatut(e.target.value)} aria-label="Statut du contenu">
          <option value="">Tous les statuts</option>
          {STATUTS_CONTENU.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button className="qc-btn" onClick={() => setCampagnes(true)}><i className="bi bi-flag"></i>Campagnes</button>
        <button className="qc-btn qc-btn--primary" onClick={preparer}>+ Préparer un contenu</button>
      </div>
      {contenus === null ? <Chargement /> : contenus.length === 0 ? (
        <div className="qc-card qc-empty">Aucun contenu{statut || recherche ? " ne correspond à ces critères" : " pour l’instant"}.</div>
      ) : (
        <div className="cm-content-grid">
          {contenus.map((c, i) => (
            <article key={c.id} className="cm-content-card">
              <div className={`cm-creative cm-creative--${i % 3}${c.media_type === "image" ? " cm-creative--image" : ""}`}
                style={c.media_type === "image" ? { backgroundImage: `url(${c.media_url})` } : undefined}>
                <span>QUICK LEARNING</span>
                <strong>{c.titre}</strong>
                <small>Apprendre n’a jamais été aussi aisé</small>
              </div>
              <div className="cm-content-info">
                <div className="cm-chips"><Chip>{c.format_label} · {c.canal_label}</Chip><Chip ton={TON_CONTENU[c.statut]}>{c.statut_label}</Chip></div>
                <h3>{c.campagne?.titre || c.titre}</h3>
                <div className="qc-foot" style={{ marginTop: 0 }}>{c.date_souhaitee ? dateHeure(c.date_souhaitee) : "Date non fixée"}</div>
                <button className="qc-btn qc-btn--sm" onClick={() => ouvrir(c)}>Ouvrir la fiche</button>
              </div>
            </article>
          ))}
        </div>
      )}
      <div className="qc-card" style={{ marginTop: 18 }}>
        <h3>Validation avant diffusion</h3>
        <div className="cm-flow">Brouillon <i className="bi bi-arrow-right"></i> À valider <i className="bi bi-arrow-right"></i> Validé <i className="bi bi-arrow-right"></i> Publié</div>
        <div className="qc-foot">Un contenu soumis arrive dans la messagerie des super admins. Une modification du texte, du média ou du lien d’un contenu validé relance la validation.</div>
      </div>
      {elements}
      {campagnes && <Campagnes onClose={() => { setCampagnes(false); charger(); }} />}
    </div>
  );
}

// ── Calendrier éditorial ────────────────────────────────────────────────────

export function CMCalendrierPage() {
  const aujourdhui = jourCameroun();
  const [lundi, setLundi] = useState(lundiDe(aujourdhui));
  const [contenus, setContenus] = useState(null);
  const dimanche = ajouterJours(lundi, 6);
  const charger = useCallback(() => {
    chargerContenus(`?debut=${lundi}&fin=${dimanche}`).then(setContenus).catch(() => setContenus([]));
  }, [lundi, dimanche]);
  useEffect(charger, [charger]);
  const { ouvrir, preparer, elements } = useFiches(charger);
  const jours = useMemo(() => Array.from({ length: 7 }, (_, i) => ajouterJours(lundi, i)), [lundi]);
  const parJour = useMemo(() => {
    const groupes = {};
    (contenus || []).forEach((c) => {
      // Jour de publication réelle s'il tombe dans la semaine, sinon jour souhaité.
      const publie = c.date_publication && jourCameroun(c.date_publication);
      const jour = publie && publie >= lundi && publie <= dimanche ? publie : jourCameroun(c.date_souhaitee);
      (groupes[jour] = groupes[jour] || []).push(c);
    });
    return groupes;
  }, [contenus, lundi, dimanche]);
  const publies = (contenus || []).filter((c) => c.statut === "publie").length;

  return (
    <div className="qc">
      <Entete titre="Calendrier éditorial" sousTitre="Organiser les contenus, leurs dates et leur validation.">
        <button className="qc-btn qc-btn--primary" onClick={preparer}>+ Préparer un contenu</button>
      </Entete>
      <div className="cm-weekbar">
        <div className="qc-toolbar" style={{ margin: 0 }}>
          <button className="qc-btn qc-btn--sm" onClick={() => setLundi(ajouterJours(lundi, -7))} aria-label="Semaine précédente"><i className="bi bi-chevron-left"></i></button>
          <Chip>{libelleSemaine(lundi)}</Chip>
          <button className="qc-btn qc-btn--sm" onClick={() => setLundi(ajouterJours(lundi, 7))} aria-label="Semaine suivante"><i className="bi bi-chevron-right"></i></button>
          {lundi !== lundiDe(aujourdhui) && <button className="qc-btn qc-btn--sm" onClick={() => setLundi(lundiDe(aujourdhui))}>Cette semaine</button>}
        </div>
        <span className="qc-foot" style={{ margin: 0 }}>Horaires Cameroun · UTC+1</span>
      </div>
      {contenus === null ? <Chargement /> : (
        <div className="cm-calendar">
          {jours.map((jour) => (
            <div key={jour} className={`cm-day${jour === aujourdhui ? " cm-day--today" : ""}`}>
              <div className="cm-day-title">{nomJour(jour)} <strong>{numeroJour(jour)}</strong></div>
              {(parJour[jour] || []).map((c) => (
                <button key={c.id} className={`cm-event cm-event--${c.statut}`} onClick={() => ouvrir(c)}>
                  <small>{heureCameroun(c.date_publication || c.date_souhaitee)} · {c.canal_label}</small>
                  <strong>{c.titre}</strong>
                  <Chip ton={TON_CONTENU[c.statut]}>{c.statut_label}</Chip>
                </button>
              ))}
              {!(parJour[jour] || []).length && <div className="cm-day-empty">Aucune publication prévue</div>}
            </div>
          ))}
          <div className="cm-day">
            <h3 style={{ fontSize: "1.05rem", fontWeight: 800 }}>À retenir</h3>
            <p className="qc-subtitle">{contenus.length} publication{contenus.length > 1 ? "s" : ""} prévue{contenus.length > 1 ? "s" : ""} dans la semaine, {publies} déjà publiée{publies > 1 ? "s" : ""}.</p>
            <div className="qc-foot">Une date prévue ne déclenche pas une publication automatique.</div>
          </div>
        </div>
      )}
      <div className="qc-card" style={{ marginTop: 18 }}>
        <h3>Un circuit partagé</h3>
        <div className="cm-flow">Community Manager : prépare <i className="bi bi-arrow-right"></i> Super admin : valide <i className="bi bi-arrow-right"></i> CM : publie et enregistre le lien</div>
        <div className="qc-foot">La date souhaitée et la date de publication réelle sont conservées séparément.</div>
      </div>
      {elements}
    </div>
  );
}
